import { CrosspointDataService, Viewport, Axis } from "@o2d/datasource";
import { container } from "tsyringe";
import { Interval, DateTime } from "luxon";
import { ResourcesViewportQueryClient } from "./resources-viewport-query-client";
import type { ResourcePlanningScheduleDto } from "./resources-viewport-query-client";
import type {
    RestApiCrosspointDataServiceConfig,
    ViewportQueryRequest
} from "../../../connectivity/viewport-query.types";
import type { ResourcePlanningItem } from "../types/resource-planning-item.type";
import { IssueRepository } from "../../../domain/repositories/issue.repository";

/**
 * CrosspointDataService for the resources viewport query endpoint.
 * Fetches SID-based schedule DTOs and expands them into ResourcePlanningItems
 * by loading full Issue entities from the IssueRepository (EntityManager cache).
 */
export class ResourcesCrosspointDataService<TXValue = unknown, TYValue = unknown>
    extends EventTarget
    implements CrosspointDataService<ResourcePlanningItem> {

    private readonly client: ResourcesViewportQueryClient;
    private readonly issueRepository: IssueRepository;
    private abortController: AbortController | null = null;

    constructor(
        private readonly config: RestApiCrosspointDataServiceConfig,
        private readonly xAxis: Axis<TXValue, any>,
        private readonly yAxis: Axis<TYValue, any>
    ) {
        super();
        this.client = container.resolve(ResourcesViewportQueryClient);
        this.issueRepository = container.resolve(IssueRepository);
    }

    /**
     * Fetches resource planning schedule data and expands it into ResourcePlanningItems.
     * Each schedule DTO × scheduledRange produces one ResourcePlanningItem.
     * Issues are batch-loaded from IssueRepository (EntityManager cache-first).
     */
    async fetchData(viewport: Viewport): Promise<ResourcePlanningItem[]> {
        this.cancelPendingRequests();
        this.abortController = new AbortController();

        const xAxisConfig = this.config.xAxisConfig(this.xAxis, viewport, 'x');
        const yAxisConfig = this.config.yAxisConfig(this.yAxis, viewport, 'y');

        const request: ViewportQueryRequest = {
            xAxis: xAxisConfig,
            yAxis: yAxisConfig
        };

        try {
            const response = await this.client.query(request, this.abortController.signal);
            return await this.expandScheduleItems(response.items);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                throw error;
            }
            throw error;
        }
    }

    /**
     * Cancels any pending requests.
     */
    cancelPendingRequests(): void {
        this.abortController?.abort();
        this.abortController = null;
    }

    /**
     * Expands schedule DTOs into ResourcePlanningItems by loading issues
     * from the repository and creating one item per scheduled range.
     */
    private async expandScheduleItems(
        items: ResourcePlanningScheduleDto[]
    ): Promise<ResourcePlanningItem[]> {
        const uniqueSids = [...new Set(items.map(item => item.issueSid))];
        const issueEntities = await this.issueRepository.findById(uniqueSids);
        const issueMap = new Map(issueEntities.map(issue => [issue.uid, issue]));

        const result: ResourcePlanningItem[] = [];

        for (const dto of items) {
            const issue = issueMap.get(dto.issueSid);
            if (!issue) {
                continue;
            }

            for (const range of dto.scheduledRanges) {
                result.push({
                    uid: `${dto.issueSid}_${dto.employeeSid}_${range.start}`,
                    issue,
                    meta: {
                        isFixed: dto.isFixed,
                        isOverdue: dto.isOverdue,
                        timeBudgetRemainingAtSlot: dto.timeBudgetRemainingAtSlot,
                        timeBudget: dto.timeBudget
                    },
                    interval: Interval.fromDateTimes(
                        DateTime.fromISO(range.start),
                        DateTime.fromISO(range.end)
                    ),
                    employees: [{ uid: dto.employeeSid }]
                });
            }
        }

        return result;
    }
}
